import { Scope } from 'logard';
import VehicleStatus from '@/dossier/constants/VehicleStatus';
import WorkStatus from '@/dossier/constants/WorkStatus';
import { removeBy, toggleArrayItem } from '@/shared/utils';
import { saneEnum } from '@/shared/utils/sanitizers';
import { isAnyOf, present } from '@/shared/utils/typescript';
import StatusBoardClient from '../clients/StatusBoardClient';
import StatusBoardCardItem from '../constants/StatusBoardCardItem';
import StatusBoardGrouping from '../constants/StatusBoardGrouping';
import { CommonDossierSearchCriteria, DossierSearchByVehicleStatusCriteria, DossierSearchByWorkStatusCriteria, DossierSearchCriteria } from '../models/DossierSearchCriteria';
import StatusBoardColumn from '../models/StatusBoardColumn';
import StatusBoardContext from '../models/StatusBoardContext';
import StatusBoardSettings from '../models/StatusBoardSettings';

const SETTINGS_STORAGE_KEY = 'statusBoardSettings';

class StatusBoardService {
  async parseSettings(scope: Scope): Promise<StatusBoardSettings> {
    const settings = this.loadSettings();
    settings.grouping = present(scope.getQueryParam('grouping', saneEnum(StatusBoardGrouping), settings.grouping));
    this.saveSettings(settings);
    return settings;
  }

  private loadSettings(): StatusBoardSettings {
    const json = localStorage.getItem(SETTINGS_STORAGE_KEY);
    const data = json ? JSON.parse(json) : {};
    return Object.assign(new StatusBoardSettings(), data);
  }

  private saveSettings(settings: StatusBoardSettings): void {
    const json = JSON.stringify(settings);
    localStorage.setItem(SETTINGS_STORAGE_KEY, json);
  }

  /**
   * Load all active columns (based on settings) with initial page of dossiers.
   * To be called everytime filters or settings are changed.
   */
  async loadInitial(context: StatusBoardContext): Promise<void> {
    context.columns = await Promise.all(this.getCriteria(context).map(async criteria => {
      const page = await StatusBoardClient.search(criteria);
      const status = 'workStatus' in criteria ? criteria.workStatus : criteria.vehicleStatus;
      return new StatusBoardColumn(status, criteria, page);
    }));
  }

  /**
   * Load the next page of dossiers for the given column.
   */
  async loadMore(column: StatusBoardColumn): Promise<void> {
    const page = await StatusBoardClient.search(column.criteria, present(column.nextCursor, 'no more available'));
    column.addPage(page);
  }

  private getCriteria(context: StatusBoardContext): Array<DossierSearchCriteria> {
    switch (context.settings.grouping) {
      case StatusBoardGrouping.WORK_STATUS:
        return context.settings.workStatuses.map(workStatus => this.getWorkStatusCriteria(workStatus, context));

      case StatusBoardGrouping.VEHICLE_STATUS:
        return context.settings.vehicleStatuses.map(vehicleStatus => this.getVehicleStatusCriteria(vehicleStatus, context));
    }
  }

  private getWorkStatusCriteria(workStatus: WorkStatus, context: StatusBoardContext): DossierSearchByWorkStatusCriteria {
    return {
      ...this.getCommonCriteria(context),
      workStatus,
      vehicleStatuses: context.filters.vehicleStatuses,
    };
  }

  private getVehicleStatusCriteria(vehicleStatus: VehicleStatus, context: StatusBoardContext): DossierSearchByVehicleStatusCriteria {
    return {
      ...this.getCommonCriteria(context),
      vehicleStatus,
      workStatuses: context.filters.workStatuses,
    };
  }

  private getCommonCriteria(context: StatusBoardContext): CommonDossierSearchCriteria {
    return {
      workshopTeamIds: context.filters.activeTeamIds,
      outletId: context.filters.common.getFirstActiveOutlet().id,
      tagIds: context.filters.common.activeDossierTagIds,
      brandIds: context.filters.activeBrandIds,
      assignedUserIds: context.filters.common.activeUserIds,
      assignedGroupIds: context.filters.common.activeGroupIds,
      includeContacts: context.settings.visibleItems.includes(StatusBoardCardItem.CONTACTS),
      includeMechanics: context.settings.visibleItems.includes(StatusBoardCardItem.MECHANICS),
    };
  }

  async toggleItem(context: StatusBoardContext, item: StatusBoardCardItem): Promise<void> {
    toggleArrayItem(context.settings.visibleItems, item);
    if (isAnyOf(item, StatusBoardCardItem.CONTACTS, StatusBoardCardItem.MECHANICS) && context.settings.visibleItems.includes(item)) {
      await this.loadInitial(context);
    }
    this.saveSettings(context.settings);
  }

  async toggleWorkStatus(context: StatusBoardContext, status: WorkStatus): Promise<void> {
    toggleArrayItem(context.settings.workStatuses, status);
    if (isAnyOf(status, ...context.settings.workStatuses)) {
      const criteria = this.getWorkStatusCriteria(status, context);
      const page = await StatusBoardClient.search(criteria);
      context.columns.push(new StatusBoardColumn(status, criteria, page));
    } else {
      removeBy(context.columns, c => c.status === status);
    }
    this.saveSettings(context.settings);
  }

  async toggleVehicleStatus(context: StatusBoardContext, status: VehicleStatus): Promise<void> {
    toggleArrayItem(context.settings.vehicleStatuses, status);
    if (isAnyOf(status, ...context.settings.vehicleStatuses)) {
      const criteria = this.getVehicleStatusCriteria(status, context);
      const page = await StatusBoardClient.search(criteria);
      context.columns.push(new StatusBoardColumn(status, criteria, page));
    } else {
      removeBy(context.columns, c => c.status === status);
    }
    this.saveSettings(context.settings);
  }
}

export default new StatusBoardService();